/**
 * Bridge to the existing confetti engine in public/js/games/victory_confetti.js.
 *
 * Phase 1: thin adapter that calls the global `victoryConfetti(opts)` if the page already loaded it,
 * otherwise no-op. A full port to TypeScript can replace this without touching call sites.
 *
 * Mapping ConfettiLevel → engine tier:
 *   off  → no call
 *   low  → tier 1
 *   mid  → tier 3
 *   high → tier 5
 *   epic → tier 7 + extra bursts
 */
type ConfettiLevel = 'off' | 'low' | 'mid' | 'high' | 'epic';

declare global {
  interface Window {
    // Existing global from victory_confetti.js; signature is intentionally loose.
    victoryConfetti?: (opts: Record<string, unknown>) => void;
  }
}

const TIER: Record<Exclude<ConfettiLevel, 'off'>, number> = {
  low: 1,
  mid: 3,
  high: 5,
  epic: 7,
};

export const ConfettiAdapter = {
  fire(level: ConfettiLevel, anchor?: HTMLElement): void {
    if (level === 'off') return;
    const fn = window.victoryConfetti;
    if (!fn) return;

    const tier = TIER[level];
    fn({ tier, result: 'win', mode: 'confettiOnly', anchor });

    // Epic gets a second delayed burst for extra punch.
    if (level === 'epic') {
      window.setTimeout(() => fn({ tier, result: 'win', mode: 'confettiOnly', anchor }), 600);
    }
  },
};
